import React, { useState } from 'react';
import {
  formatCount,
  humanize,
  SUPPRESSED_HINT,
  SUPPRESSED_LABEL,
} from './format';

const DIMENSIONS = [
  { key: 'gender', label: 'Gender' },
  { key: 'region', label: 'Region' },
  { key: 'country', label: 'Country' },
  { key: 'age_band', label: 'Age' },
  { key: 'education', label: 'Education' },
];

const isMuted = (value) => value === 'Unknown' || value === 'Other';

// Buckets arrive in a fixed order from the API, so colours and order stay the
// same from year to year. Suppressed buckets stay visible as "< 5" so
// partners can see the group exists.
function BreakdownPanel({ title, subtitle, breakdowns, showGraduates }) {
  const available = DIMENSIONS.filter(
    (dimension) => (breakdowns?.[dimension.key] ?? []).length > 0
  );
  const [selected, setSelected] = useState(available[0]?.key);
  const activeKey = available.some((d) => d.key === selected)
    ? selected
    : available[0]?.key;
  const buckets = breakdowns?.[activeKey] ?? [];
  const maxPercent = Math.max(...buckets.map((b) => b.percent ?? 0), 1);

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {available.length > 1 && (
          <div
            className="flex flex-wrap gap-1 bg-gray-50 border border-gray-200 rounded-lg p-1"
            role="tablist"
          >
            {available.map((dimension) => (
              <button
                key={dimension.key}
                type="button"
                role="tab"
                aria-selected={dimension.key === activeKey}
                onClick={() => setSelected(dimension.key)}
                className={`px-3 py-1 rounded-md text-xs transition-colors ${
                  dimension.key === activeKey
                    ? 'bg-white text-claret-500 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {dimension.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {buckets.length === 0 ? (
        <p className="text-sm text-gray-500">No data for this breakdown.</p>
      ) : (
        <div>
          <div
            className={`grid ${
              showGraduates
                ? 'grid-cols-[9rem_1fr_7rem_6rem]'
                : 'grid-cols-[9rem_1fr_7rem]'
            } gap-3 pb-2 border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-500`}
          >
            <span>Group</span>
            <span>Share of learners</span>
            <span className="text-right">Learners</span>
            {showGraduates && <span className="text-right">Graduates</span>}
          </div>
          <ul className="list-none p-0 m-0">
            {buckets.map((bucket) => {
              const suppressed = bucket.enrolled === null;
              return (
                <li
                  key={bucket.value}
                  className={`grid ${
                    showGraduates
                      ? 'grid-cols-[9rem_1fr_7rem_6rem]'
                      : 'grid-cols-[9rem_1fr_7rem]'
                  } gap-3 items-center py-2 border-b border-gray-50 last:border-0 text-sm`}
                >
                  <span
                    className={`truncate ${
                      isMuted(bucket.value) ? 'text-gray-500' : 'text-gray-800'
                    }`}
                  >
                    {humanize(bucket.value)}
                  </span>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    {suppressed ? (
                      <div className="h-full w-full border border-dashed border-gray-300 rounded-full bg-white" />
                    ) : (
                      <div
                        className={`h-full rounded-full ${
                          isMuted(bucket.value)
                            ? 'bg-gray-400'
                            : 'bg-persian-500'
                        }`}
                        style={{
                          width: `${
                            bucket.enrolled > 0
                              ? Math.max(
                                  ((bucket.percent ?? 0) / maxPercent) * 100,
                                  1
                                )
                              : 0
                          }%`,
                        }}
                      />
                    )}
                  </div>
                  <span
                    className="text-right text-xs tabular-nums text-gray-700"
                    title={suppressed ? SUPPRESSED_HINT : undefined}
                  >
                    {suppressed ? (
                      <span className="text-gray-500">{SUPPRESSED_LABEL}</span>
                    ) : (
                      <>
                        <span className="font-semibold">
                          {formatCount(bucket.enrolled)}
                        </span>{' '}
                        <span className="text-gray-400">
                          ({(bucket.percent ?? 0).toFixed(1)}%)
                        </span>
                      </>
                    )}
                  </span>
                  {showGraduates && (
                    <span
                      className="text-right text-xs tabular-nums text-gray-700"
                      title={
                        bucket.graduated === null ? SUPPRESSED_HINT : undefined
                      }
                    >
                      {formatCount(bucket.graduated)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

export default BreakdownPanel;
